import { UpdateBenefitRequest, useToast } from '@aslanpay/portal-common-lib';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import { authenticatedPaths } from 'app/constants/routes';
import useAuthContext from 'app/hooks/useAuthContext';
import useBenefits from 'app/hooks/useBenefits';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AddEditBenefitForm, {
  AddEditBenefitFormFields,
} from 'app/components/Benefits/AddEditBenefitForm';
import { Spinner } from '@chakra-ui/react';

import buildBenefitPayload from '../AddBenefit/AddBenefit.function';

function EditBenefit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { company } = useAuthContext();
  const toast = useToast();
  const params = useParams();

  const companyId = company?.id || '';
  const benefitId = params.benefitId || '';

  const { data: benefit, isLoading } = useBenefits.useFetchBenefit(companyId, params.benefitId);

  const { mutate: editBenefit, isPending: isSaving } = useBenefits.useUpdateBenefit(company?.id);

  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  const handleBack = () => navigate(-1);

  const leaveAfterSave = () => {
    if (returnTo) {
      navigate(-1);
      return;
    }
    navigate(authenticatedPaths.benefitsManager.path, { replace: true });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const showApiErrors = (error: any, fallbackMessage: string) => {
    const subErrors = error?.response?.data?.subErrors;
    if (Array.isArray(subErrors) && subErrors.length > 0) {
      subErrors.forEach((subError: { message?: string }) => {
        if (subError.message) {
          toast({
            position: 'top-right',
            status: 'error',
            title: 'Error',
            description: subError.message,
          });
        }
      });
      return;
    }

    const errorMessage = error?.response?.data?.message;
    toast({
      position: 'top-right',
      status: 'error',
      title: 'Error',
      description: typeof errorMessage === 'string' ? errorMessage : fallbackMessage,
    });
  };

  const createDraftBenefit = (info: AddEditBenefitFormFields) => {
    editBenefit(
      { benefitId, body: buildBenefitPayload(info, 'DRAFT') as UpdateBenefitRequest },
      {
        onSuccess: () => {
          toast({ position: 'top-right', status: 'success', title: 'Draft saved.' });
          leaveAfterSave();
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => showApiErrors(error, 'Failed to save draft.'),
      }
    );
  };

  const handlePublish = (info: AddEditBenefitFormFields) => {
    editBenefit(
      { benefitId, body: buildBenefitPayload(info, 'PUBLISHED') as UpdateBenefitRequest },
      {
        onSuccess: () => {
          toast({ position: 'top-right', status: 'success', title: 'Benefit published.' });
          leaveAfterSave();
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => showApiErrors(error, 'Failed to publish benefit.'),
      }
    );
  };

  return (
    <AuthenticatedPageWrapper title="Edit benefit">
      {
        // eslint-disable-next-line no-nested-ternary
        !isLoading ? (
          benefit ? (
            <AddEditBenefitForm
              handleBack={handleBack}
              publishBenefit={handlePublish}
              saveDraftBenefit={createDraftBenefit}
              benefit={benefit}
              isSaving={isSaving}
            />
          ) : null
        ) : (
          <Spinner size="lg" />
        )
      }
    </AuthenticatedPageWrapper>
  );
}

export default EditBenefit;
